import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { AlertData, Order } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";
import { EnhancedClipsTableHead } from "./EnhancedClipsTableHead";
import {
  formatClipReceivedLocal,
  formatDateInUTC,
} from "./ClipsUtilities/formatTimeStamp";
import { handleRowSelected } from "./ClipsUtilities/handleRowSelected";
import { NavigateFunction, useSearchParams } from "react-router-dom";
import { handleButtonShading } from "./ClipsUtilities/handleButtonShading";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { handleReorderIndex } from "../Alerts/AlertsUtils/handleReorderIndex";
import { convertToMilitary } from "../HealthMonitoring/HealthMonitoringUtils/getAllHMData";
import {
  removeClipsIdParamsFromUrl,
} from "./ClipsUtilities/clipsUrlParams";

export const ClipsTable = ({
  clipsData,
  setClipsData,
  setSelectedRow,
  page,
  setPage,
  setVideoClipLoading,
  setVideoURL,
  scrollToTop,
  setVerifiedClicked,
  setUnverifiedClicked,
  selectedRow,
  setClipsIndexOrder,
  userFilters,
  navigate,
}: {
  clipsData: any;
  setClipsData: any;
  setSelectedRow: any;
  page: any;
  setPage: any;
  setVideoClipLoading: any;
  setVideoURL: any;
  scrollToTop: any;
  setVerifiedClicked: Dispatcher<boolean>;
  setUnverifiedClicked: Dispatcher<boolean>;
  selectedRow: any;
  setClipsIndexOrder: any;
  userFilters: any;
  navigate: NavigateFunction;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("formatted_date");
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const lastAutoPagedSelectionRef = React.useRef<string | null>(null);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - clipsData.length) : 0;

  const sortingFunction = stableSort(
    clipsData,
    getComparator(order, orderBy)
  ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const visibleRows = React.useMemo(
    () => sortingFunction,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, orderBy, page, rowsPerPage, clipsData]
  );

  React.useEffect(() => {
    const sortedRows = stableSort(clipsData, getComparator(order, orderBy));
    const selectedIndex = sortedRows.findIndex((row: any) => row.selected);
    const selectedFileId: string | null =
      selectedIndex >= 0 && sortedRows[selectedIndex]?.file_id != null
        ? String(sortedRows[selectedIndex].file_id)
        : null;
    const expectedPage =
      selectedIndex >= 0 ? Math.floor(selectedIndex / rowsPerPage) : 0;

    if (
      selectedFileId &&
      selectedFileId !== lastAutoPagedSelectionRef.current &&
      expectedPage !== page
    ) {
      lastAutoPagedSelectionRef.current = selectedFileId;
      setPage(expectedPage);
    }
  }, [clipsData, order, orderBy, page, rowsPerPage, setPage]);

  React.useEffect(() => {
    handleReorderIndex(
      stableSort(clipsData, getComparator(order, orderBy)),
      setClipsIndexOrder,
      clipsData,
      setClipsData,
      "",
      "All"
    );
  }, [order, visibleRows]);

  return (
    <Paper className="overflow-scroll scrollbar-hide w-[100%]">
      <TableContainer sx={{ maxHeight: 490, width: 755 }}>
        <Table
          sx={{
            fontFamily: "Mulish",
            postion: "absolute",
            tableLayout: "fixed",
          }}
          aria-labelledby="clips-table"
          stickyHeader
        >
          <EnhancedClipsTableHead
            onRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
          />
          <TableBody>
            {visibleRows.map((row: any, index: number) => {
              let sx: any = { cursor: "pointer" };
              if (row.selected) {
                sx = {
                  cursor: "pointer",
                  backgroundColor: "rgba(2, 136, 209, 0.2)",
                };
              }
              return (
                <TableRow
                  hover
                  onClick={() => {
                    scrollToTop();
                    setVideoClipLoading(true);
                    setSelectedRow(row);
                    localStorage.setItem(
                      "clipsSelectedRow",
                      JSON.stringify(row)
                    );
                    handleButtonShading(
                      row,
                      setVerifiedClicked,
                      setUnverifiedClicked
                    );
                    handleRowSelected(
                      row,
                      setSelectedRow,
                      setVideoURL,
                      setVideoClipLoading,
                      clipsData,
                      setClipsData,
                      selectedRow,
                      userFilters,
                      "ailink",
                      userFilters.user,
                      navigate
                    );
                    removeClipsIdParamsFromUrl(setSearchParams, searchParams);
                  }}
                  tabIndex={-1}
                  key={row.id}
                  sx={sx}
                >
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.customer_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.camera_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.clip_header_received
                      ? formatDateInUTC(row.clip_header_received)
                      : ""}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {formatClipReceivedLocal(
                      row.clip_header_received,
                      row.clip_received_local,
                      row.site_timezone
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.clip_result}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.file_id}
                  </TableCell>
                  {/* <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >{row.ground_truth}</TableCell> */}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={clipsData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
